/**
 * main.ts — エントリポイント
 *
 * 責務:
 *   1. SCSS を import して CSS バンドルに含める
 *   2. 共通コンポーネントを初期化
 *   3. body[data-page] を見てページ固有クラスを初期化
 */

// ---- スタイル -------------------------------------------------------
import '../scss/style.scss';

// ---- コンポーネント -------------------------------------------------
import { Drawer } from './components/Drawer';
import { Modal } from './components/Modal';
import { SmoothScroll } from './components/SmoothScroll';
import { DomHelper } from './modules/DomHelper';
import { ScrollAnimation } from './modules/ScrollAnimation';
import { Utility } from './modules/Utility';

import { BrandDetailPage } from './pages/BrandDetailPage';
import { BrandPage } from './pages/BrandPage';
import { CaseDetailPage } from './pages/CaseDetailPage';
import { CasePage } from './pages/CasePage';
import { ColumnDetailPage } from './pages/ColumnDetailPage';
import { ColumnPage } from './pages/ColumnPage';
import { ContactPage } from './pages/ContactPage';
import { ContactThanksPage } from './pages/ContactThanksPage';
import { FaqPage } from './pages/FaqPage';
// ---- ページ固有 -----------------------------------------------------
import { HomePage } from './pages/HomePage';
import { LookbookPage } from './pages/LookbookPage';
import { MicroPage } from './pages/MicroPage';
import { NewsDetailPage } from './pages/NewsDetailPage';
import { NewsPage } from './pages/NewsPage';
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage';

// ============================================================
// ドロワー アコーディオン（ブランド サブメニュー）
// Accordion.ts と同じ scrollHeight 方式で実装
// ============================================================
function initDrawerAccordion(): void {
  const triggers = DomHelper.qsa<HTMLButtonElement>('.js-drawer-accordion');

  triggers.forEach((trigger) => {
    const controlsId = trigger.getAttribute('aria-controls');
    if (!controlsId) return;

    const target = document.getElementById(controlsId);
    if (!target) return;

    // 初期化: transition と初期高さをインラインで設定
    target.style.transition = 'height 400ms ease';
    target.style.height = trigger.getAttribute('aria-expanded') === 'true' ? 'auto' : '0';

    trigger.addEventListener('click', () => {
      const isExpanded = trigger.getAttribute('aria-expanded') === 'true';

      if (isExpanded) {
        // 閉じる: padding-bottom を先に除去してから height を 0 へアニメーション
        // ※ height: 0 のまま padding-bottom が残ると要素が潰れないため先に外す
        const currentHeight = target.offsetHeight;
        target.classList.remove('is-open'); // padding-bottom を即時除去
        target.style.height = `${currentHeight}px`; // 現在の高さを明示的に固定
        void target.offsetHeight; // reflow を強制
        target.style.height = '0';
        trigger.setAttribute('aria-expanded', 'false');
      } else {
        // 開く: is-open を先に付けて padding-bottom 込みの高さを測定
        target.classList.add('is-open');
        target.style.height = `${target.scrollHeight}px`;
        trigger.setAttribute('aria-expanded', 'true');
        target.addEventListener(
          'transitionend',
          () => {
            target.style.height = 'auto';
          },
          { once: true }
        );
      }
    });
  });
}

// ============================================================
// アクティブナビゲーション
// ============================================================
function setActiveNavLinks(): void {
  const currentPath = window.location.pathname;

  const selectors = ['.l-header__nav-link', '.l-drawer__link'];
  selectors.forEach((selector) => {
    DomHelper.qsa<HTMLAnchorElement>(selector).forEach((link) => {
      const href = link.getAttribute('href');
      if (!href) return;
      const isHome = href === '/' && currentPath === '/';
      const isSubPage = href !== '/' && currentPath.startsWith(href);
      if (isHome || isSubPage) {
        link.classList.add('is-active');
        link.setAttribute('aria-current', 'page');
      }
    });
  });
}

// ============================================================
// 共通初期化
// ============================================================
function initCommon(): void {
  Utility.setViewportHeight();
  window.addEventListener(
    'resize',
    Utility.debounce(() => {
      Utility.setViewportHeight();
    }, 200) as EventListener
  );

  try {
    new Drawer();
  } catch {
    // ドロワー要素がないページでは何もしない
  }

  initDrawerAccordion();
  Modal.initAll();
  new SmoothScroll();
  ScrollAnimation.initAll();

  DomHelper.qsa<HTMLAnchorElement>('a').forEach((link) => {
    const href = link.getAttribute('href') ?? '';
    if (Utility.isExternalLink(href) && !link.hasAttribute('target')) {
      link.setAttribute('target', '_blank');
      link.setAttribute('rel', 'noopener noreferrer');
    }
  });
}

// ============================================================
// ページ固有初期化
// body の data-page 属性でルーティング
// ============================================================
function initPage(): void {
  const pageName = DomHelper.getPageName();

  switch (pageName) {
    case 'home':
      new HomePage().init();
      break;
    case 'brand':
      new BrandPage().init();
      break;
    case 'brand-detail':
      new BrandDetailPage().init();
      break;
    case 'lookbook':
      new LookbookPage().init();
      break;
    case 'news':
      new NewsPage().init();
      break;
    case 'news-detail':
      new NewsDetailPage().init();
      break;
    case 'column':
      new ColumnPage().init();
      break;
    case 'column-detail':
      new ColumnDetailPage().init();
      break;
    case 'case':
      new CasePage().init();
      break;
    case 'case-detail':
      new CaseDetailPage().init();
      break;
    case 'faq':
      new FaqPage().init();
      break;
    case 'contact':
      new ContactPage().init();
      break;
    case 'contact-thanks':
      new ContactThanksPage().init();
      break;
    case 'privacy-policy':
      new PrivacyPolicyPage().init();
      break;
    case 'micro':
      new MicroPage().init();
      break;
    default:
      break;
  }
}

// ============================================================
// エントリ
// ============================================================
Utility.ready(() => {
  setActiveNavLinks();
  initCommon();
  initPage();
});
